import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getInboxBill, signInboxBill, rejectInboxBill } from "../api/accounting";
import { MyBillSignHeader } from "../features/acct/MyBillSignHeader";
import { MyBillSignAmount } from "../features/acct/MyBillSignAmount";
import { MyBillSignDetails } from "../features/acct/MyBillSignDetails";
import { MyBillSignInfo } from "../features/acct/MyBillSignInfo";
import { MyBillSignTerms } from "../features/acct/MyBillSignTerms";
import { MyBillSignActions } from "../features/acct/MyBillSignActions";
import { SignatureCanvas } from "../features/pay/SignatureCanvas";
import { useApiError } from "../hooks/useApiError";

// صفحة استلام مبلغ — رأس مثبَّت + مبلغ + تفاصيل الفاتورة + التسليم + الاسم + التوقيع + الشروط.
export default function MyBillSignPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { id } = useParams();
  const describe = useApiError();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sig, setSig] = useState(null);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { getInboxBill(id).then(setData).catch((e) => setError(describe(e))); }, [id, describe]);

  const confirm = async () => {
    if (!sig) { toast.error(t("acct.bills.sign.needSig")); return; }
    if (!terms) { toast.error(t("acct.bills.mysign.needTerms")); return; }
    setBusy(true);
    try { await signInboxBill(id, sig); toast.success(t("acct.bills.mysign.confirmed")); nav("/"); }
    catch (e) { toast.error(describe(e)); } finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt(t("acct.bills.mysign.rejectAsk"), "");
    if (!reason || !reason.trim()) return;
    setBusy(true);
    try { await rejectInboxBill(id, reason.trim()); toast(t("acct.bills.mysign.rejected")); nav("/"); }
    catch (e) { toast.error(describe(e)); } finally { setBusy(false); }
  };

  if (error) return (
    <div className="mx-auto mt-20 max-w-md rounded-2xl border-2 border-rose-400 bg-rose-50 p-6 text-center">
      <p className="text-lg font-black text-rose-600" data-testid="my-sign-error">{error}</p>
    </div>
  );
  if (!data) return <div className="grid min-h-screen place-content-center text-sm">…</div>;

  const b = data.bill;
  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="my-bill-sign-page">
      <MyBillSignHeader bill={b} />
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <MyBillSignAmount bill={b} company={data.company} cashbox={data.cashbox} branch={data.branch} />
        <MyBillSignDetails bill={b} cashbox={data.cashbox} branch={data.branch} />
        <MyBillSignInfo bill={b} cashbox={data.cashbox} sentBy={data.sent_by} sentAt={data.sent_at} />
        <div className="rounded-2xl border-2 border-emerald-200 bg-white p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-black text-emerald-700">
            👤 {t("acct.bills.mysign.receiverName")}
          </p>
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-3">
            <p className="text-center text-lg font-black text-emerald-900" data-testid="my-receiver-name">
              {b.receiver_name || "—"}
            </p>
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">{t("acct.bills.mysign.nameAuto")}</p>
        </div>
        <div className="rounded-2xl border-2 border-emerald-200 bg-white p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-black text-emerald-700">
            ✍️ {t("acct.bills.sign.canvas")}
          </p>
          <SignatureCanvas onChange={setSig} color="#065F46" height={220} />
        </div>
        <MyBillSignTerms terms={terms} setTerms={setTerms} />
      </div>
      <MyBillSignActions busy={busy} canConfirm={Boolean(sig && terms)} onConfirm={confirm} onReject={reject} />
    </div>
  );
}
